import { useEffect } from 'react';
import { createUuid4 } from '@gov-nx/utils/common';
import { useSessionStorage } from '@gov-nx/utils/web';

// STORED_TABS is a map of { [tabId]: lastSeenMs }
const TAB_ID_KEY = 'TAB_ID';
const storedTabsKey = 'STORED_TABS';
const HEARTBEAT_INTERVAL_MS = 5_000;
// 120 s gives a background tab room to survive Chrome's intensive timer-throttling
// (which clamps setInterval to ~1 min after 5 min in the background) without
// being falsely pruned and triggering logout while the user still has a live tab.
const TAB_TTL_MS = 120_000;

export const useTabTracking = () => {
	const tabId = useSessionStorage(TAB_ID_KEY);
	const TAB_ID_VALUE = tabId.value ?? createUuid4(TAB_ID_KEY);

	const getActiveTabs = (): Record<string, number> => {
		const stored = localStorage.getItem(storedTabsKey);
		try {
			return stored ? JSON.parse(stored) : {};
		} catch {
			return {};
		}
	};

	// Pure filter — returns live entries without writing to localStorage
	const computeLiveTabs = (): Record<string, number> => {
		const tabs = getActiveTabs();
		const now = Date.now();
		const live: Record<string, number> = {};
		for (const [id, lastSeen] of Object.entries(tabs)) {
			if (now - lastSeen < TAB_TTL_MS) {
				live[id] = lastSeen;
			}
		}
		return live;
	};

	// Prunes stale entries then writes a fresh timestamp for this tab (single write)
	const writeHeartbeat = () => {
		const tabs = computeLiveTabs();
		tabs[TAB_ID_VALUE] = Date.now();
		localStorage.setItem(storedTabsKey, JSON.stringify(tabs));
	};

	useEffect(() => {
		tabId.set(TAB_ID_VALUE);
		writeHeartbeat();
		const intervalId = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);
		return () => clearInterval(intervalId);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	const removeActiveTab = ({
		onLastTabRemoved,
	}: {
		onLastTabRemoved: () => void;
	}) => {
		const tabs = computeLiveTabs();
		delete tabs[TAB_ID_VALUE];
		localStorage.setItem(storedTabsKey, JSON.stringify(tabs));

		if (Object.keys(tabs).length === 0) {
			onLastTabRemoved();
		}
	};

	return {
		tabId: TAB_ID_VALUE,
		activeTabs: getActiveTabs(),
		removeActiveTab,
		// Called on bfcache restore (pageshow.persisted) to re-register the tab
		updateActiveTabCount: writeHeartbeat,
	};
};
